import { useEffect, useMemo, useState } from 'react';
import { persistPar, useClaimArmedRun, useTimer } from '../lib/timer/provider';
import { userErrorText } from '../lib/userErrorText';

interface Props {
  onBack: () => void;
  /** Shooter whose Score screen opened this one — a run started here is theirs. */
  runFor?: string | null;
}

function fmtTime(at: number): string {
  const d = new Date(at);
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}.${d.getMilliseconds().toString().padStart(3, '0')}`;
}

function describeDecoded(decoded: unknown): string {
  if (decoded == null) return '';
  if (typeof decoded === 'string') return decoded;
  if (typeof decoded === 'object' && 'kind' in decoded) {
    const d = decoded as { kind: string } & Record<string, unknown>;
    switch (d.kind) {
      case 'session_started':
        return `SESSION_STARTED · sess=${d.sessId} · delay=${(d.startDelayDS as number) / 10}s`;
      case 'session_set_begin':
        return `SESSION_SET_BEGIN · sess=${d.sessId}`;
      case 'shot_detected':
        return `SHOT #${d.shotNum} · t=${((d.shotTimeMs as number) / 1000).toFixed(3)}s`;
      case 'session_suspended':
        return `SESSION_SUSPENDED · ${d.totalShots} Schuss`;
      case 'session_resumed':
        return `SESSION_RESUMED · ${d.totalShots} Schuss`;
      case 'session_stopped':
        return `SESSION_STOPPED · ${d.totalShots} Schuss`;
    }
  }
  return JSON.stringify(decoded);
}

/**
 * Status / debug / control panel for the SG Timer. Reachable from the
 * header chip. Splits into three blocks:
 *
 *   1. Status + Connect/Disconnect — top of screen, always visible.
 *   2. PAR setup + Test controls — only when connected, lets the user
 *      drive a session straight from the PWA without opening a stage.
 *   3. Help guide (collapsible, German) — pairing instructions and
 *      browser-support caveats. Open by default when BLE is unavailable
 *      so iPad users immediately see why the connect button is greyed.
 */
export function TimerDebug({ onBack, runFor = null }: Props) {
  const {
    source,
    status,
    rawLog,
    bleSupported,
    isConnected,
    pairing,
    error,
    connect,
    disconnect,
    claimRun,
  } = useTimer();
  // Opened from a shooter's Score screen: a run started here (or on the
  // timer's own button meanwhile) counts for that shooter when they go back.
  useClaimArmedRun(runFor);

  const [delayS, setDelayS] = useState<string>(() => '3,0');
  const [timeLimitS, setTimeLimitS] = useState<string>(() => '0');
  const [shotLimit, setShotLimit] = useState<string>('5');
  const [helpOpen, setHelpOpen] = useState<boolean>(!bleSupported);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!bleSupported) setHelpOpen(true);
  }, [bleSupported]);

  function parseSeconds(s: string, def = 0): number {
    const v = Number(s.replace(',', '.'));
    if (!Number.isFinite(v) || v < 0) return def;
    return Math.round(v * 10);
  }
  function parseInt0(s: string): number {
    const v = Number(s);
    return Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0;
  }

  async function runWithBusy(label: string, fn: () => Promise<void>) {
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      console.error(`${label} failed`, err);
    } finally {
      setBusy(null);
    }
  }

  const onConnect = () =>
    runWithBusy('connect', async () => {
      await connect();
    });
  const onDisconnect = () =>
    runWithBusy('disconnect', async () => {
      await disconnect();
    });
  const onTestStart = () =>
    runWithBusy('start', async () => {
      const startDS = parseSeconds(delayS, 30);
      const timeDS = parseSeconds(timeLimitS, 0);
      const limit = parseInt0(shotLimit);
      source.setExpectedShots(limit > 0 ? limit : null);
      source.setPar(startDS, timeDS);
      persistPar(startDS, timeDS);
      await source.start();
      if (runFor != null) claimRun(runFor);
    });
  const onSuspend = () => runWithBusy('suspend', () => source.suspend());
  const onResume = () => runWithBusy('resume', () => source.resume());
  const onStop = () => runWithBusy('stop', () => source.stop());
  const onResetPad = () => source.reset();

  const reversedLog = useMemo(() => [...rawLog].reverse(), [rawLog]);

  const statusColor = status.reconnecting
    ? 'bg-amber-600'
    : status.disconnected || !isConnected
      ? 'bg-red-700'
      : status.state === 'running'
        ? 'bg-emerald-600'
        : status.state === 'armed'
          ? 'bg-amber-600'
          : 'bg-zinc-600';

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Timer</h2>
        <button
          onClick={onBack}
          className="rounded border border-zinc-600 px-3 py-1 text-sm text-zinc-300 hover:border-zinc-400"
        >
          ← Zurück
        </button>
      </header>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
        <div className="flex items-center gap-3">
          <span className={`inline-block h-3 w-3 rounded-full ${statusColor}`} />
          <div className="flex-1">
            <div className="font-medium">
              {status.reconnecting
                ? 'Verbindung wird wiederhergestellt…'
                : isConnected
                  ? (status.deviceName ?? 'SG Timer verbunden')
                  : status.disconnected
                    ? 'Verbindung verloren'
                    : 'Nicht verbunden'}
            </div>
            <div className="text-xs text-zinc-400">
              Status: <span className="font-mono">{status.state}</span>
              {status.apiVersion ? <> · API v{status.apiVersion}</> : null}
              {status.lastSessId != null ? <> · sess {status.lastSessId}</> : null}
              {status.shots.length > 0 ? <> · {status.shots.length} Schuss</> : null}
            </div>
          </div>
          {!isConnected ? (
            <button
              onClick={() => void onConnect()}
              disabled={!bleSupported || pairing || busy === 'connect'}
              className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-40"
            >
              {pairing ? 'Koppeln…' : 'Timer koppeln'}
            </button>
          ) : (
            <button
              onClick={() => void onDisconnect()}
              disabled={busy === 'disconnect'}
              className="rounded-lg border border-zinc-600 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-400 disabled:opacity-40"
            >
              Trennen
            </button>
          )}
        </div>
        {error && (
          <div className="mt-2 rounded bg-red-900/30 px-3 py-2 text-xs text-red-200">
            Fehler: {userErrorText(error, 'ble')}
          </div>
        )}
        {!bleSupported && (
          <div className="mt-2 rounded bg-amber-900/30 px-3 py-2 text-xs text-amber-200">
            Web Bluetooth ist in diesem Browser nicht verfügbar (z.&nbsp;B. Safari/iOS). Siehe Hilfe
            unten.
          </div>
        )}
      </div>

      {isConnected && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
          <h3 className="mb-3 text-sm font-bold text-zinc-300">PAR-Setup &amp; Testlauf</h3>
          <div className="grid grid-cols-3 gap-2">
            <label className="block">
              <span className="text-xs text-zinc-500">Startverzögerung (s)</span>
              <input
                type="text"
                inputMode="decimal"
                value={delayS}
                onChange={(e) => setDelayS(e.target.value)}
                className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-2 text-base tabular-nums"
              />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-500">Zeitlimit (s, 0 = kein)</span>
              <input
                type="text"
                inputMode="decimal"
                value={timeLimitS}
                onChange={(e) => setTimeLimitS(e.target.value)}
                className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-2 text-base tabular-nums"
              />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-500">Schusslimit (0 = kein)</span>
              <input
                type="text"
                inputMode="numeric"
                value={shotLimit}
                onChange={(e) => setShotLimit(e.target.value)}
                className="mt-1 w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-2 text-base tabular-nums"
              />
            </label>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <button
              onClick={() => void onTestStart()}
              disabled={busy != null}
              className="rounded-lg bg-emerald-700 px-3 py-3 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-40"
            >
              ▶ SESSION_START
            </button>
            <button
              onClick={() => void onSuspend()}
              disabled={busy != null || status.state !== 'running'}
              className="rounded-lg border border-zinc-600 px-3 py-3 text-sm text-zinc-200 hover:border-zinc-400 disabled:opacity-30"
            >
              ⏸ Pause
            </button>
            <button
              onClick={() => void onResume()}
              disabled={busy != null || status.state === 'running'}
              className="rounded-lg border border-zinc-600 px-3 py-3 text-sm text-zinc-200 hover:border-zinc-400 disabled:opacity-30"
            >
              ▷ Fortsetzen
            </button>
            <button
              onClick={() => void onStop()}
              disabled={busy != null}
              className="rounded-lg bg-red-700 px-3 py-3 text-sm font-bold text-white hover:bg-red-600 disabled:opacity-40"
            >
              ■ SESSION_STOP
            </button>
          </div>
          <button onClick={onResetPad} className="mt-2 text-xs text-zinc-500 hover:text-zinc-300">
            ↺ Lokalen Schussbuffer leeren
          </button>
          <p className="mt-2 text-xs text-zinc-500">
            Ein Testlauf zählt mit, falls ein Training läuft. „Schusslimit" gilt auch hier:
            Überschuss wird ignoriert, beim letzten Schuss wird automatisch gestoppt.
          </p>
        </div>
      )}

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="text-sm font-bold text-zinc-300">Ereignis-Log</h3>
          <span className="text-xs text-zinc-500">{rawLog.length} Einträge</span>
        </div>
        {reversedLog.length === 0 ? (
          <div className="text-xs text-zinc-500">Noch keine Daten.</div>
        ) : (
          <ul className="max-h-72 space-y-1 overflow-auto font-mono text-[11px]">
            {reversedLog.map((entry, idx) => (
              <li
                key={`${entry.at}-${idx}`}
                className={`flex flex-wrap items-baseline gap-2 ${entry.dir === 'in' ? 'text-zinc-200' : 'text-amber-200'}`}
              >
                <span className="text-zinc-500">{fmtTime(entry.at)}</span>
                <span className="text-zinc-500">{entry.dir === 'in' ? '←' : '→'}</span>
                <span className="text-zinc-500">{entry.char}</span>
                <span>{entry.hex}</span>
                {entry.decoded && (
                  <span className="text-emerald-300">{describeDecoded(entry.decoded)}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
        <button
          onClick={() => setHelpOpen((o) => !o)}
          className="flex w-full items-center justify-between text-left text-sm font-bold text-zinc-300"
        >
          <span>Wie verbinde ich den SG Timer?</span>
          <span>{helpOpen ? '−' : '+'}</span>
        </button>
        {helpOpen && (
          <div className="mt-3 space-y-3 text-sm text-zinc-300">
            <p>
              Unterstützt werden <strong>SG Timer Sport</strong> (Geräte­name beginnt mit
              <span className="font-mono"> SG-SST4A…</span>) und <strong>SG Timer GO</strong> (
              <span className="font-mono">SG-SST4B…</span>) per Web Bluetooth, BLE-API v3.2.
            </p>

            <div>
              <h4 className="font-bold text-zinc-200">Browser</h4>
              <ul className="ml-5 list-disc space-y-1 text-zinc-400">
                <li>
                  Chrome oder Edge auf Desktop, Chrome auf Android. Auf <strong>iOS/Safari</strong>{' '}
                  gibt es <em>kein</em> Web Bluetooth — bitte Stoppuhr- oder Manuell-Modus
                  verwenden.
                </li>
                <li>
                  Im Produktivbetrieb läuft die App per HTTPS. Lokal (
                  <span className="font-mono">http://localhost:5002</span>) ist Web Bluetooth in
                  Chrome ohne Flag erlaubt.
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-zinc-200">Erstes Pairing</h4>
              <ol className="ml-5 list-decimal space-y-1 text-zinc-400">
                <li>Timer einschalten und in den Bluetooth-Modus bringen.</li>
                <li>
                  In dieser App auf <strong>„Timer koppeln"</strong> tippen — Chrome öffnet einen
                  Geräte­dialog. Pairing erfordert eine Nutzergeste; ein Auto-Connect ohne Tap ist
                  nicht möglich.
                </li>
                <li>
                  Im Dialog den Eintrag <span className="font-mono">SG-SST4A…</span> bzw.{' '}
                  <span className="font-mono">SG-SST4B…</span> auswählen und bestätigen.
                </li>
                <li>
                  Sobald die Statuskugel oben grün leuchtet, ist der Timer gekoppelt — ab jetzt
                  steht in der Wertung die Registerkarte „SG Timer" zur Verfügung.
                </li>
              </ol>
            </div>

            <div>
              <h4 className="font-bold text-zinc-200">Tablet wach halten</h4>
              <p className="text-zinc-400">
                Wenn das Display abdunkelt, drosselt das Betriebssystem BLE-Benachrichtigungen. App
                zur Startseite hinzufügen (Standalone) und im Range-Betrieb das Auto-Lock
                deaktivieren.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-zinc-200">Probleme?</h4>
              <ul className="ml-5 list-disc space-y-1 text-zinc-400">
                <li>
                  Browser zeigt den Timer nicht: Akkustand prüfen, in Reichweite halten,
                  Site-Berechtigung in den Browser-Einstellungen zurücksetzen und neu koppeln.
                </li>
                <li>
                  Mehrere Stände auf demselben Gerät: nach dem Wechsel auf{' '}
                  <strong>„Trennen"</strong> tippen, sonst hält der Browser den letzten Timer offen.
                </li>
                <li>
                  Der Timer piept, aber kein Schuss erscheint im Log: „Schusslimit" prüfen —
                  Überschuss wird absichtlich verworfen.
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
